import * as React from 'react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell,  { tableCellClasses }  from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import Tooltip from '@mui/material/Tooltip';
import { openResources } from '../authConfig';
import { Link, useLocation } from 'react-router-dom';

interface Data {
    dateTime: string;
    loadGroup: string;
    [key: string]: any;
}

interface DemandResponseCurrentStatusProps {
  coop: string;
  defaultStartDate: Date;
  defaultEndDate: Date
}

const getColor = (value: string) => {
  if (value === 'ON') return 'rgb(0, 95, 0)';
  if (value === 'OFF') return 'rgb(139, 0, 0)';
  return 'inherit';
}

const getTextColor = (value: string) => {
  if (value === 'ON') return 'white';
  if (value === 'OFF') return 'white';
  return 'black';
}

const toolTipDataLoadGroup: Record<string, string> = {
  '1': 'Short-Term Loads (water heaters)',
  '2': 'Intermediate-Term Loads (storage heat)',
  '3': 'Long-Term Loads (dual heating furnaces, back-up generators)',
  '6': 'Summer-Only Loads (irrigation, cycled air conditioning)',
  '9.99': 'Internal Network Transmission',
}

const toolTipData: Record<string, Record<string, string>> = {
  '1.01': {
    '9': 'Grain Dryers',
    '10': 'Pk Ind Light',
    '11': 'Water Heaters',
    '12': 'Water Heaters',
    '13': 'Water Heaters',
    '14': 'Water Heaters',
    '15': 'Clothes Dryer',
    '16': 'Office Heat',
    '17': 'Space Heat',
    '18': 'Space Heat',
    '19': 'Warehouse Fans',
    '20': 'Warehouse Fans',
    '21': 'Warehouse Heat',
    '22': 'Warehouse Heat',
    '23': 'Warehouse Heat',
    '24': 'Warehouse Heat',
  },
  '1.02': {
    '11': 'Water Heaters',
    '12': 'Water Heaters',
    '13': 'Water Heaters',
    '14': 'Water Heaters',
    '15': 'Misc Heat 1',
    '16': 'Misc Heat 1',
    '17': 'Water Heaters',
    '18': 'Water Heaters',
    '19': 'Water Heaters',
    '20': 'Water Heaters',
    '24': 'Ind. Controlled Loads 1',
  },
  '2.01': {
    '9': 'Slab Heat',
    '10': 'Slab Heat',
    '11': 'Slab Heat',
    '12': 'Slab Heat',
    '13': 'Slab Heat',
    '14': 'Slab Heat',
    '15': 'Misc Heat 2',
    '16': 'Misc Heat 2',
    '17': 'Thermal Storage Heat',
    '18': 'Thermal Storage Heat',
    '19': 'Water Heaters',
    '20': 'Water Heaters',
    '21': 'Thermal Storage Heat',
    '22': 'Thermal Storage Heat',
    '23': 'Thermal Storage Heat',
    '24': 'Ind. Controlled Loads 2',
  },
  '2.02': {
    '12': 'Misc Loads 2',
    '13': 'Misc Loads 2',
    '19': 'Water Heaters',
    '20': 'Water Heaters',
    '24': 'Ind. Controlled Loads 2',
  },
  '2.03': {
    '9': 'Slab Heat',
    '10': 'Slab Heat',
    '11': 'Slab Heat',
    '12': 'Slab Heat',
    '13': 'Slab Heat',
    '14': 'Slab Heat',
    '15': 'Misc Heat 2',
    '16': 'Misc Heat 2',
    '17': 'Thermal Storage Heat',
    '18': 'Thermal Storage Heat',
    '19': 'Water Heaters',
    '20': 'Water Heaters',
    '21': 'Thermal Storage Heat',
    '22': 'Thermal Storage Heat',
    '23': 'Thermal Storage Heat',
    '24': 'Ind. Controlled Loads 2',
  },
  '2.04': {
    '12': 'Misc Loads 2',
    '13': 'Misc Loads 2',
    '19': 'Water Heaters',
    '20': 'Water Heaters',
    '24': 'Ind. Controlled Loads 2',
  },
  '2.06': {
    '9': 'Battery Storage',
    '16': 'Battery Storage',
  },
  '3.01': {
    '9': 'Dual Heat',
    '10': 'Dual Heat',
    '11': 'Dual Heat',
    '12': 'Dual Heat',
    '13': 'Dual Heat',
    '14': 'Dual Heat',
    '15': 'Dual Heat',
    '16': 'Dual Heat',
    '17': 'Misc Heat 3',
    '18': 'Misc Heat 3',
    '19': 'Dual Heat',
    '20': 'Dual Heat',
    '21': 'Dual Heat',
    '22': 'Dual Heat',
    '24': 'Ind. Controlled Loads 3',
  },
  '3.06': {
    '9': 'Interruptible Alert',
    '10': 'Industrial Loads',
    '11': 'Commercial',
    '12': 'Commercial',
    '13': 'Commercial',
    '14': 'Commercial',
    '15': 'Industrial',
    '16': 'Commercial',
    '17': 'Commercial',
    '18': 'Commercial',
    '19': 'Commercial',
    '23': 'Ind. Controlled Loads 3',
    '24': 'Ind. Controlled Loads 3',
  },
  '3.07': {
    '9': 'Interruptible Alert',
    '10': 'Comm, Direct Control',
    '11': 'Comm, Direct Control',
    '12': 'Misc Loads 3',
    '13': 'Misc Loads 3',
    '14': 'Comm, Direct Control',
    '15': 'Comm, Direct Control',
    '16': 'Comm, Direct Control',
    '17': 'Misc Loads 3',
    '18': 'Misc Loads 3',
    '19': 'Comm, Direct Control',
    '20': 'Comm, Direct Control',
    '21': 'Ind Controlled Loads 3',
    '22': 'Voltage Reduction',
    '23': 'Comm, Indirect Control',
    '24': 'Ind Controlled Loads 3',
  },
  '3.08':{
    '10':'Industrial Interruptible Loads',
    '15':'Industrial Interruptible Loads',
  },
  '3.09': {
    '9': 'Dual Heat',
    '10': 'Dual Heat',
    '11': 'Dual Heat',
    '12': 'Dual Heat',
    '13': 'Dual Heat',
    '14': 'Dual Heat',
    '15': 'Dual Heat',
    '16': 'Dual Heat',
    '17': 'Misc Heat 3',
    '18': 'Misc Heat 3',
    '24': 'Ind. Contr Loads 3',
  },
  '6.01': {
    '9':'Industrial Interruptible Loads',
    '10':'Low Temperature Grain Dryers',
    '11':'Reserved for Future Use',
    '12':'Miscellaneous Loads',
    '13':'Miscellaneous Loads',
    '14':'Reserved for Future Use',
    '15':'Reserved for Future Use',
    '16':'Reserved for Future Use',
    '17':'Reserved for Future Use',
    '18':'Reserved for Future Use',
    '19':'Reserved for Future Use',
    '20':'Irrigation Incremental Pricing Plan',
    '21':'Residential Locally Cycled Air Conditioning Loads',
    '22':'Commercial Locally Cycled Air Conditioning Loads',
    '23':'Indirectly Controlled Residential Loads',
    '24':'Indirectly Controlled Commercial Loads',
  },
};

const columnHeaders = [
  "Date Time",
  "Load Group",
  "DO9", "DO10", "DO11", "DO12", "DO13", "DO14", "DO15",
  "DO16", "DO17", "DO18", "DO19", "DO20", "DO21", "DO22",
  "DO23", "DO24"
];

const getToolTipTitle = (loadGroup: string, doubleOrder: string): string => {
  return toolTipData[loadGroup]?.[doubleOrder] ?? 'Unknown';
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      width: 50,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  export default function DemandResponseCurrentDetail({coop, defaultStartDate, defaultEndDate}: DemandResponseCurrentStatusProps) {

    const [data, setData] = useState<Data[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(20);
    const [startDate, setStartDate] = React.useState<Date | null>(defaultStartDate);
    const [endDate, setEndDate] = React.useState<Date | null>(defaultEndDate);
    const location = useLocation();
    const queryString = location.search;

    //const baseUrl = 'https://api.minnkota.com';
    //const endpoint = '/demandresponse/loadmanagement/currentstatus';

    useEffect(() => {
      // Function to fetch data from the API
      const fetchData = async () => {
          try {            
           
            const formatDate = (date: Date) => {
                return date.toLocaleString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                });
              };
              //`${baseUrl}${endpoint}`
            const response = await axios.get(openResources.status.endpoint, {
              params: {
                area: coop, //sessionStorage.getItem('coop') || 'Nodak',
                //startDate: startDate ? startDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0], //.split('T')[0]
                //endDate: endDate ? endDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]  //.split('T')[0]
              }
            });
            if (response.data && Array.isArray(response.data)) {
              const adaptedData: Data[] = response.data.map((item: any) => ({
                dateTime: formatDate(new Date(item.dateTime)),
                loadGroup: item.loadGroup, //.replace(/\./g, "")
                ...Object.fromEntries(
                  Object.entries(item).map(([key, value]) => 
                    key.startsWith('dO') ? [`D${key.substring(2)}`, value] : [key, value]
                )
              )
              }));
              setData(adaptedData);
            } else {
              setData([]);
            }    
           
          } catch (error: any) {
              setError('Error fetching data: ' + error.message);
          } finally {
              setLoading(false);
          }
      };

      fetchData();
  }, [startDate, endDate]);

  useEffect(() => {
    if (data.length === 0 && page > 0) {
      setStartDate((prevDate) => {
        const newDate = new Date(prevDate!);
        newDate.setDate(newDate.getDate() - 1);
        return newDate;
      });
      setEndDate((prevDate) => {
        const newDate = new Date(prevDate!);
        newDate.setDate(newDate.getDate() - 1);
        return newDate;
      });
    }
  }, [data.length, page]);

    if (loading) return <div>Loading...</div>;
    if (error) return <div>{error}</div>;

    return (
      <div style={{ maxWidth:2000, margin:'auto' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2, paddingTop: 2 }}>
            <DatePicker sx={{ display: 'none'}}
              label="Start Date"
              value={startDate}
              onChange={(newValue) => {
                setStartDate(newValue);
              }}
              //renderInput={(params) => <TextField {...params} />}
            />
            <DatePicker sx={{ display: 'none'}}
              label="End Date"
              value={endDate}
              onChange={(newValue) => {
                setEndDate(newValue);
              }}
              //renderInput={(params) => <TextField {...params} />}
            />
          </Box>
          <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'white' }}>
                <StyledTableRow>
                  {columnHeaders.map((header, index) => (
                    <StyledTableCell key={index} style={{ textAlign: 'center' }}>
                      {header}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, rowIndex) => (
                  <StyledTableRow key={rowIndex}>
                    {/* Date Time & Load Group Cells */}
                    <Tooltip title={`Date: ${row.dateTime}`}>
                      <StyledTableCell style={{ textAlign: 'center', paddingTop: '0px', paddingBottom: '0px' }}>
                        {row.dateTime}
                      </StyledTableCell>
                    </Tooltip>

                    <Tooltip title={toolTipDataLoadGroup[row.loadGroup.replace(/\.\d+$/, "")] || 'Unknown'}>
                      <StyledTableCell style={{ textAlign: 'center', paddingTop: '0px', paddingBottom: '0px' }}>
                        <Link to={`/ripple/${row.loadGroup}${queryString}`} style={{ textDecoration:'none'}}>
                          {row.loadGroup}
                        </Link>
                      </StyledTableCell>
                    </Tooltip>

                    {/* Double Order (DO) Columns */}
                    {columnHeaders.slice(2).map((header, colIndex) => {
                      const doubleOrder = header.replace("DO", ""); // Extract number from "DO9" -> "9"
                      const value = row[`D${doubleOrder}`];

                      return (
                        <Tooltip key={colIndex} title={getToolTipTitle(row.loadGroup, doubleOrder)}>
                          <StyledTableCell 
                            style={{ 
                              backgroundColor: getColor(value), 
                              color: getTextColor(value), 
                              textAlign: 'center', 
                              paddingTop: '0px', 
                              paddingBottom: '0px' 
                            }}
                          >
                            {value}
                          </StyledTableCell>
                        </Tooltip>
                      );
                    })}
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt:2, width: '100%' }}>
            <div className="load-group-container">
              <div className="load-group-categories">
                <h3>{"Load Group Categories"}</h3>
                <p>{"1 : Short-Term Loads(water heaters)"}</p>
                <p>{"2 : Intermediate-Term Loads (storage heat)"}</p>
                <p>{"3 : Long-Term Loads (dual heating furnaces, back-up generators)"}</p>
                <p>{"6 : Summer-Only Loads (irrigation, cycled air conditioning)"}</p>
                <p>{"9.99 : Internal Network Transmission"}</p>
              </div>
              <div className="load-group-categories">
                <h3>{"Status"}</h3>
                <p>{"ON : Device is not being controlled."}</p>
                <p>{"OFF : Device is being controlled."}</p>
                <p>{"--- : N/A or status defaults to previous state."}</p>
              </div>
            </div>
          </Box>
        </LocalizationProvider>
      </div>
  );
}